import { Injectable, computed, inject, signal } from '@angular/core';
import { SistemaEnum, SistemaId } from '@nomoh-monorepo/shared-types';
import { IEnvironment } from './environment.interface';
import { ENVIRONMENT_TOKEN } from './environment.token';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  private environment = inject(ENVIRONMENT_TOKEN);

  // Signals para reatividade (KISS)
  private _currentEnv = signal<IEnvironment>(this.environment);

  // Computed signals para acesso fácil
  $apiUrls = computed(() => this._currentEnv().apiUrls);
  $frontendUrls = computed(() => this._currentEnv().frontend);
  $isProduction = computed(() => this._currentEnv().production);
  $isDevelopment = computed(() => !this._currentEnv().production);
  $authConfig = computed(() => this._currentEnv().auth);
  $features = computed(() => this._currentEnv().features);
  $appConfig = computed(() => this._currentEnv().app);
  $internalDomains = computed(() => this._currentEnv().internalDomains);

  // Métodos utilitários (KISS)
  getApiUrl(
    service: 'bardo' | 'nomoh' | 'zap' | 'nomohCliente' | 'bardoCliente'
  ): string {
    return this.$apiUrls()[service];
  }

  getFrontendUrl(sistema: SistemaId): string {
    switch (sistema) {
      case SistemaEnum.Nomoh: // Nomoh
        return this.$frontendUrls()['nomohAdmin'];
      case SistemaEnum.Bardo: // Bardo
        return this.$frontendUrls()['bardoAdmin'];
      case SistemaEnum.NomohCliente: // Nomoh Cliente
        return this.$frontendUrls()['nomohCliente'];
      default:
        throw new Error('Sistema desconhecido');
    }
  }

  getCadastroUrl(sistema: SistemaId): string {
    const base = this.getFrontendUrl(sistema);
    return `${base}${base.endsWith('/') ? '' : '/'}cadastrar`;
  }

  getApiUrlWithEnum(sistema: SistemaId): string {
    switch (sistema) {
      case SistemaEnum.Nomoh: // Nomoh
        return this.$apiUrls()['nomoh'];
      case SistemaEnum.Bardo: // Bardo
        return this.$apiUrls()['bardo'];
      case SistemaEnum.ApiZap: // Zap
        return this.$apiUrls()['zap'];
      case SistemaEnum.NomohCliente: // Nomoh Cliente
        return this.$apiUrls()['nomoh'];
      default:
        throw new Error('Sistema desconhecido');
    }
  }

  isFeatureEnabled(feature: keyof IEnvironment['features']): boolean {
    return this.$features()[feature];
  }

  getLogLevel(): string {
    return this.$appConfig().logLevel;
  }

  /**
   * Retorna a lista de domínios internos configurados para o ambiente
   * Usado pelo httpConfigInterceptor para determinar onde enviar credentials
   */
  getInternalDomains(): string[] {
    return this.$internalDomains();
  }

  // Para debug (apenas em desenvolvimento)
  logEnvironment(): void {
    if (this.$isDevelopment()) {
    }
  }
}
